import React, { useEffect, useState } from 'react';
import { Button, Icon, Label, Message, Popup, Segment, Table } from 'semantic-ui-react';
import { consultarFluxo, consultarVtme, erroDeColeta, pedirFluxo, priorizarFluxo, retentarFluxo, retentarVtme } from '../../services/coletaApi';
import { ColetaFluxo, ColetaFluxoContrato, ColetaFluxoLemit, ColetaFluxoResultado, ColetaJob, ColetaVtmeCliente, ColetaVtmeLado, ColetaVtmePessoa } from '../../types/coleta';
import {
  filaColetaOcupada,
  fluxoColetaAceito,
  MENSAGEM_COLETA_SEM_FILA,
  temDadoDeColeta,
  visibilidadeBotaoAtualizarOrigem,
  visibilidadeBotaoPriorizar,
  visibilidadeBotoesColeta,
} from './coletaBotoesVisibilidade';
import {
  blocoCnpjVtme,
  deveEnfileirarVtme,
  jobVtmeComResultado,
  ladosDoContratoAberto,
  linhasTabelaVtme,
  temIdsPedidoVtme,
} from './coletaVtmeVisibilidade';
import { formatCnpj } from '../../services/utils/stringUtils';
import { estadoDaFatura, visaoFaturaP2b } from './coletaFaturaEstado';
import { avisoLemitQualidade, contratoAssinadoOuDepois, textoDaData, textoDaFila } from './coletaFluxoTexto';
import {
  lemitParaTela,
  mostrarSecaoLemit,
  numerosDoLemit,
  temDadoLemit,
  temResultadoLemit,
  textoVazioLemit,
} from './coletaLemitVisibilidade';
import {
  ItemResumoQualidade,
  LinhaResumoQualidade,
  adminEncontradoNoP2b,
  chaveTelefoneBr,
  resumoQualidade,
} from './coletaResumoQualidade';
import { RESULTADOS_ID_PEDIDO, resumoVtmeAgrupado, textoIdPedido } from './coletaResumoVtme';
import { descricaoDaRegra } from './descricaoRegraQualidade';
import { isVtmeCriadoOrLaterPipelineStatus } from '../../utils/contractStatusUtils';

const INTERVALO_MS = 5000;

const CORES: Record<string, string> = {
  verde: '#21ba45',
  amarelo: '#fbbd08',
  vermelho: '#db2828',
};

const caixa = {
  marginBottom: '20px',
  background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)',
  border: '2px solid #4A90E2',
  borderRadius: '12px',
  padding: '20px',
  boxShadow: '0 4px 12px rgba(74, 144, 226, 0.3)',
};

const titulo = { fontSize: '1.2em', fontWeight: 700, color: '#2c3e50' } as const;
const texto = { marginTop: '10px', fontSize: '1em', color: '#2c3e50', lineHeight: 1.8, fontWeight: 600 } as const;
const celulaCabecalho = {
  fontSize: '0.95em',
  fontWeight: 700,
  color: '#2c3e50',
  padding: '14px 10px',
  borderRight: '2px solid #B0C4DE',
} as const;
const celula = {
  padding: '12px 10px',
  fontSize: '0.95em',
  color: '#2c3e50',
  borderRight: '1px solid #e0e0e0',
} as const;
const secaoP2b = {
  marginTop: 16,
  background: '#fff',
  border: '2px solid #4A90E2',
  borderRadius: 12,
  padding: 16,
  boxShadow: '0 2px 8px rgba(74, 144, 226, 0.2)',
} as const;
const secaoCustcode = {
  marginTop: 12,
  background: '#f7f9fc',
  border: '1px solid #B0C4DE',
  borderRadius: 10,
  overflow: 'hidden',
} as const;

const falhou = (fluxo: ColetaFluxo | null) =>
  fluxo?.fila?.estado === 'erro' || Boolean(fluxo?.fila?.falha);

const chaveVtmeDe = (contractId?: string, groupId?: string | null) => {
  const grupo = (groupId || '').trim();
  if (grupo) return `grupo:${grupo}`;
  const contrato = (contractId || '').trim();
  if (contrato) return `contrato:${contrato}`;
  return '';
};

const jobVtmeAberto = (job: ColetaJob | null) =>
  job?.estado === 'na_fila' || job?.estado === 'em_andamento';

const statusDe = (falha: unknown) => (
  falha && typeof falha === 'object' && 'response' in falha
    ? (falha as { response?: { status?: number } }).response?.status
    : undefined
);

function blocos(resultado: ColetaFluxoResultado): ColetaFluxoContrato[] {
  if (resultado.contratos && resultado.contratos.length > 0) return resultado.contratos;
  const linhas = resultado.linhas ?? [];
  const contatos = resultado.contatos ?? [];
  if (linhas.length === 0 && contatos.length === 0 && !resultado.telefoneLemit && !resultado.inadimplente) {
    return [];
  }
  return [{
    custcode: '',
    contatos,
    telefoneLemit: resultado.telefoneLemit,
    linhas,
  }];
}

function valorOuTraco(valor?: string | null): string {
  const textoValor = (valor || '').trim();
  return textoValor || '—';
}

function separarDdd(numero?: string | null): { ddd: string; numero: string } {
  const digitos = (numero || '').replace(/\D/g, '');
  if (digitos.length < 2) return { ddd: '—', numero: digitos };
  return { ddd: digitos.slice(0, 2), numero: digitos.slice(2) };
}

function calendarioSaoPaulo(instante: Date): { ano: number; mes: number; dia: number } | null {
  if (Number.isNaN(instante.getTime())) return null;
  const partes = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instante);
  const pegar = (tipo: Intl.DateTimeFormatPartTypes) =>
    Number(partes.find((parte) => parte.type === tipo)?.value);
  const ano = pegar('year');
  const mes = pegar('month');
  const dia = pegar('day');
  if (!ano || !mes || !dia) return null;
  return { ano, mes, dia };
}

function dataDaAtivacao(valor: string): { ano: number; mes: number; dia: number } | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(valor);
  if (!match) return null;
  const dia = Number(match[1]);
  const mes = Number(match[2]);
  const ano = Number(match[3]);
  const prova = new Date(Date.UTC(ano, mes - 1, dia));
  if (prova.getUTCFullYear() !== ano || prova.getUTCMonth() !== mes - 1 || prova.getUTCDate() !== dia) {
    return null;
  }
  return { ano, mes, dia };
}

function mesesDesdeAtivacao(ativacao?: string | null, agora: Date = new Date()): number | null {
  const inicio = dataDaAtivacao((ativacao || '').trim());
  const hoje = calendarioSaoPaulo(agora);
  if (!inicio || !hoje) return null;
  let meses = (hoje.ano - inicio.ano) * 12 + (hoje.mes - inicio.mes);
  if (hoje.dia < inicio.dia) meses -= 1;
  return meses;
}

function unidadeMeses(quantidade: number): string {
  return quantidade === 1 ? 'mês' : 'meses';
}

export function textoDaAtivacao(ativacao?: string | null, agora: Date = new Date()): string {
  const textoValor = (ativacao || '').trim();
  const meses = mesesDesdeAtivacao(textoValor, agora);
  if (meses == null) return '—';
  const decorridos = meses < 0 ? 0 : meses;
  return `${textoValor} (ativo há ${decorridos} ${unidadeMeses(decorridos)})`;
}

export function textoDaFidelidade(parcelasRestantes?: number | null): string {
  const parcelas = typeof parcelasRestantes === 'number' && !Number.isNaN(parcelasRestantes)
    ? parcelasRestantes
    : 0;
  return `${parcelas} ${unidadeMeses(parcelas)}`;
}

const Bolinha: React.FC<{ cor: string }> = ({ cor }) => {
  const nome = (cor || '').toLowerCase();
  return (
    <span
      role="img"
      aria-label={nome || 'sem cor'}
      style={{
        display: 'inline-block',
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: CORES[nome] || '#bdbdbd',
        boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
      }}
    />
  );
};

const TabelaFatura: React.FC<{ contrato: ColetaFluxoContrato }> = ({ contrato }) => {
  const contato1 = contrato.contatos?.[0] || '';
  const contato2 = contrato.contatos?.[1] || '';
  return (
    <div style={{ overflowX: 'auto', marginTop: 8 }}>
      <div style={{ background: '#fff', borderRadius: 8 }}>
        <Table celled striped unstackable>
          <Table.Header>
            <Table.Row style={{ background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)' }}>
              <Table.HeaderCell style={celulaCabecalho}>Contato 1</Table.HeaderCell>
              <Table.HeaderCell style={celulaCabecalho}>Contato 2</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell style={celula}>{valorOuTraco(contato1)}</Table.Cell>
              <Table.Cell style={celula}>{valorOuTraco(contato2)}</Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
    </div>
  );
};

const celulaDivergente = {
  ...celula,
  background: '#fdecea',
  color: '#9b1c1c',
  fontWeight: 700,
} as const;

function conferenciaDe(valor?: string): { texto: string; divergente: boolean } {
  const codigo = (valor || '').trim().toLowerCase();
  if (codigo === 'ok' || codigo === 'bateu') return { texto: 'Ok', divergente: false };
  if (codigo === 'divergente') return { texto: 'Divergente', divergente: true };
  if (!codigo) return { texto: '—', divergente: false };
  return { texto: codigo, divergente: true };
}

const TabelaVtme: React.FC<{
  colunas: string[];
  linhas: string[][];
  divergentes?: boolean[];
}> = ({ colunas, linhas, divergentes }) => {
  if (linhas.length === 0) return null;
  return (
    <div style={{ overflowX: 'auto', marginTop: 8 }}>
      <div style={{ background: '#fff', borderRadius: 8 }}>
        <Table celled striped unstackable>
          <Table.Header>
            <Table.Row style={{ background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)' }}>
              {colunas.map((coluna) => (
                <Table.HeaderCell key={coluna} style={celulaCabecalho}>{coluna}</Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {linhas.map((linha, indice) => {
              const divergente = Boolean(divergentes?.[indice]);
              const estilo = divergente ? celulaDivergente : celula;
              return (
                <Table.Row key={`${linha.join('-')}-${indice}`} style={divergente ? { background: '#fdecea' } : undefined}>
                  {linha.map((celulaTexto, col) => (
                    <Table.Cell key={`${indice}-${col}`} style={estilo}>{celulaTexto}</Table.Cell>
                  ))}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
    </div>
  );
};

function pessoasVisiveis(pessoas?: ColetaVtmePessoa[]): ColetaVtmePessoa[] {
  return (pessoas || []).filter((pessoa) => (
    (pessoa.nome || '').trim()
    || (pessoa.cpf || '').trim()
    || (pessoa.telefone || '').trim()
    || (pessoa.email || '').trim()
  ));
}

type LadoPainelVtme = {
  lado: ColetaVtmeLado;
  rotuloInterno: string;
  ladoNome: 'aditivo' | 'renegociacao';
};

type PainelVtme = {
  chave: string;
  rotulo: string;
  lados: LadoPainelVtme[];
};

function idVisivelDoLado(lado: ColetaVtmeLado | null | undefined, fallback?: string | null): string {
  return (lado?.id || fallback || '').trim();
}

function paineisVtme(opcoes: {
  mostraAditivo: boolean;
  mostraRenegociacao: boolean;
  aditivo?: ColetaVtmeLado | null;
  renegociacao?: ColetaVtmeLado | null;
  idAditivo?: string | null;
  idRenegociacao?: string | null;
}): PainelVtme[] {
  const lados: Array<LadoPainelVtme & { id: string; tipo: 'Aditivo' | 'Renegociação' }> = [];
  if (opcoes.mostraAditivo && opcoes.aditivo) {
    lados.push({
      lado: opcoes.aditivo,
      ladoNome: 'aditivo',
      rotuloInterno: '',
      id: idVisivelDoLado(opcoes.aditivo, opcoes.idAditivo),
      tipo: 'Aditivo',
    });
  }
  if (opcoes.mostraRenegociacao && opcoes.renegociacao) {
    lados.push({
      lado: opcoes.renegociacao,
      ladoNome: 'renegociacao',
      rotuloInterno: '',
      id: idVisivelDoLado(opcoes.renegociacao, opcoes.idRenegociacao),
      tipo: 'Renegociação',
    });
  }
  if (lados.length === 2 && lados[0].id && lados[0].id === lados[1].id) {
    return [{
      chave: `vtme:pedido:${lados[0].id}`,
      rotulo: `Pedido ${lados[0].id}`,
      lados: lados.map((item) => ({
        lado: item.lado,
        ladoNome: item.ladoNome,
        rotuloInterno: item.tipo,
      })),
    }];
  }
  return lados.map((item) => ({
    chave: `vtme:${item.ladoNome}:${item.id || '—'}`,
    rotulo: `${item.tipo} ${item.id || '—'}`,
    lados: [{
      lado: item.lado,
      ladoNome: item.ladoNome,
      rotuloInterno: '',
    }],
  }));
}

const AcordeaoVtme: React.FC<{
  rotulo: string;
  aberto: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ rotulo, aberto, onToggle, children }) => (
  <div style={secaoCustcode}>
    <button
      type="button"
      aria-expanded={aberto}
      onClick={onToggle}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 8,
        textAlign: 'left',
        lineHeight: 1,
        background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)',
        border: 'none',
        padding: '12px 14px',
        cursor: 'pointer',
        fontWeight: 700,
        color: '#2c3e50',
        fontSize: '1em',
      }}
    >
      <Icon
        name={aberto ? 'chevron down' : 'chevron right'}
        style={{
          margin: 0,
          lineHeight: 1,
          height: '1em',
          width: '1em',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      />
      <span style={{ lineHeight: 1 }}>{rotulo}</span>
    </button>
    {aberto ? <div style={{ padding: 14 }}>{children}</div> : null}
  </div>
);

const LadoVtme: React.FC<{
  lado: ColetaVtmeLado;
  rotuloLado: string;
  cnpjContrato: string;
  linhasContrato?: LinhaResumoQualidade[];
  ladoNome: 'aditivo' | 'renegociacao';
}> = ({ lado, rotuloLado, cnpjContrato, linhasContrato, ladoNome }) => {
  const cliente: ColetaVtmeCliente = lado.cliente || {
    cnpj: blocoCnpjVtme(lado, cnpjContrato).pedido,
    conferencia: blocoCnpjVtme(lado, cnpjContrato).ok === false ? 'divergente' : (blocoCnpjVtme(lado, cnpjContrato).ok ? 'ok' : ''),
  };
  const clienteConf = conferenciaDe(cliente.conferencia);
  const admins = pessoasVisiveis(lado.administrador);
  const reps = pessoasVisiveis(lado.replegal);
  const autorizados = pessoasVisiveis(lado.autorizados);
  const avisoId = RESULTADOS_ID_PEDIDO.has(lado.resultado)
    ? textoIdPedido(lado, ladoNome === 'aditivo' ? 'Aditivo' : 'Renegociação')
    : '';
  const linhas = linhasTabelaVtme(lado, linhasContrato, ladoNome);
  if (avisoId) {
    return (
      <div style={{ marginTop: rotuloLado ? 12 : 0 }}>
        {rotuloLado ? (
          <strong style={{ ...titulo, display: 'block', marginBottom: 8 }}>{rotuloLado}</strong>
        ) : null}
        <p style={{ ...texto, marginTop: 8 }}>{avisoId}</p>
      </div>
    );
  }
  return (
    <div style={{ marginTop: rotuloLado ? 12 : 0 }}>
      {rotuloLado ? (
        <strong style={{ ...titulo, display: 'block', marginBottom: 8 }}>{rotuloLado}</strong>
      ) : null}
      <div style={{ marginTop: 10 }}>Cliente</div>
      <TabelaVtme
        colunas={['Razão Social', 'Nome fantasia', 'CNPJ', 'Conferência']}
        linhas={[[
          valorOuTraco(cliente.razao),
          valorOuTraco(cliente.fantasia),
          cliente.cnpj ? formatCnpj(cliente.cnpj) : '—',
          clienteConf.texto,
        ]]}
        divergentes={[clienteConf.divergente]}
      />
      {admins.length > 0 ? (
        <>
          <div style={{ marginTop: 10 }}>Administrador</div>
          <TabelaVtme
            colunas={['Nome', 'CPF', 'E-mail', 'Telefone', 'Conferência']}
            linhas={admins.map((pessoa) => {
              const conf = conferenciaDe(pessoa.conferencia);
              return [valorOuTraco(pessoa.nome), valorOuTraco(pessoa.cpf), valorOuTraco(pessoa.email), valorOuTraco(pessoa.telefone), conf.texto];
            })}
            divergentes={admins.map((pessoa) => conferenciaDe(pessoa.conferencia).divergente)}
          />
        </>
      ) : null}
      {reps.length > 0 ? (
        <>
          <div style={{ marginTop: 10 }}>Representantes</div>
          <TabelaVtme
            colunas={['Nome', 'CPF', 'Telefone', 'Conferência']}
            linhas={reps.map((pessoa) => {
              const conf = conferenciaDe(pessoa.conferencia);
              return [valorOuTraco(pessoa.nome), valorOuTraco(pessoa.cpf), valorOuTraco(pessoa.telefone), conf.texto];
            })}
            divergentes={reps.map((pessoa) => conferenciaDe(pessoa.conferencia).divergente)}
          />
        </>
      ) : null}
      {autorizados.length > 0 ? (
        <>
          <div style={{ marginTop: 10 }}>Autorizados</div>
          <TabelaVtme
            colunas={['Nome', 'CPF', 'Telefone', 'Conferência']}
            linhas={autorizados.map((pessoa) => {
              const conf = conferenciaDe(pessoa.conferencia);
              return [valorOuTraco(pessoa.nome), valorOuTraco(pessoa.cpf), valorOuTraco(pessoa.telefone), conf.texto];
            })}
            divergentes={autorizados.map((pessoa) => conferenciaDe(pessoa.conferencia).divergente)}
          />
        </>
      ) : null}
      <div style={{ marginTop: 10 }}>Observações</div>
      <div style={{ marginTop: 6, fontSize: '0.95em', color: '#2c3e50', whiteSpace: 'pre-wrap', fontWeight: 500 }}>
        {valorOuTraco(lado.observacoes)}
      </div>
      {linhas.length === 0 ? null : (
        <>
          <div style={{ marginTop: 10 }}>Linhas</div>
          <TabelaVtme
            colunas={ladoNome === 'aditivo'
              ? ['DDD', 'ICCID', 'Plano', 'Valor VTME', 'Conferência']
              : ['DDD', 'Número', 'Plano', 'Valor VTME', 'Conferência']}
            linhas={linhas.map((linha) => (
              ladoNome === 'aditivo'
                ? [linha.ddd, linha.iccid, linha.plano, linha.valor, linha.conferencia]
                : [linha.ddd, linha.numero, linha.plano, linha.valor, linha.conferencia]
            ))}
            divergentes={linhas.map((linha) => linha.divergente)}
          />
        </>
      )}
    </div>
  );
};

const casaTelefone = (numero: string, alvos: string[]) => {
  const chave = chaveTelefoneBr(numero);
  if (!chave) return false;
  return alvos.some((alvo) => chaveTelefoneBr(alvo) === chave);
};

const TabelaLemitDados: React.FC<{ lemit: ColetaFluxoLemit }> = ({ lemit }) => {
  const linhas = lemit.dados ?? [];
  if (linhas.length === 0) return null;
  return (
    <>
      <div style={{ marginTop: 10 }}>Dados gerais</div>
      <div style={{ overflowX: 'auto', marginTop: 8 }}>
        <div style={{ background: '#fff', borderRadius: 8 }}>
          <Table celled striped unstackable>
            <Table.Header>
              <Table.Row style={{ background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)' }}>
                <Table.HeaderCell style={celulaCabecalho}>Campo</Table.HeaderCell>
                <Table.HeaderCell style={celulaCabecalho}>Valor</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {linhas.map((item, indice) => (
                <Table.Row key={`${item.campo}-${indice}`}>
                  <Table.Cell style={celula}>{valorOuTraco(item.campo)}</Table.Cell>
                  <Table.Cell style={celula}>{valorOuTraco(item.valor)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      </div>
    </>
  );
};

const TabelaLemitTelefones: React.FC<{ lemit: ColetaFluxoLemit; telefonesAdministrador: string[] }> = ({
  lemit,
  telefonesAdministrador,
}) => {
  const linhas = lemit.telefones ?? [];
  if (linhas.length === 0) return null;
  return (
    <>
      <div style={{ marginTop: 10 }}>Telefones</div>
      <div style={{ overflowX: 'auto', marginTop: 8 }}>
        <div style={{ background: '#fff', borderRadius: 8 }}>
          <Table celled striped unstackable>
            <Table.Header>
              <Table.Row style={{ background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)' }}>
                <Table.HeaderCell style={celulaCabecalho}>Tipo</Table.HeaderCell>
                <Table.HeaderCell style={celulaCabecalho}>DDD</Table.HeaderCell>
                <Table.HeaderCell style={celulaCabecalho}>Número</Table.HeaderCell>
                <Table.HeaderCell style={celulaCabecalho}>Situação</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {linhas.map((item, indice) => {
                const { ddd, numero } = separarDdd(item.numero);
                const bateu = casaTelefone(item.numero, telefonesAdministrador);
                const estilo = bateu ? { ...celula, color: '#1e7e34', fontWeight: 700 } : celula;
                return (
                  <Table.Row key={`${item.tipo}-${item.numero}-${indice}`}>
                    <Table.Cell style={estilo}>{valorOuTraco(item.tipo)}</Table.Cell>
                    <Table.Cell style={estilo}>{ddd}</Table.Cell>
                    <Table.Cell style={estilo}>{numero || '—'}</Table.Cell>
                    <Table.Cell style={estilo}>{valorOuTraco(item.situacao)}</Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </div>
      </div>
    </>
  );
};

const TabelaLemitSocios: React.FC<{ lemit: ColetaFluxoLemit }> = ({ lemit }) => {
  const linhas = lemit.socios ?? [];
  if (linhas.length === 0) return null;
  return (
    <>
      <div style={{ marginTop: 10 }}>Sócios</div>
      <div style={{ overflowX: 'auto', marginTop: 8 }}>
        <div style={{ background: '#fff', borderRadius: 8 }}>
          <Table celled striped unstackable>
            <Table.Header>
              <Table.Row style={{ background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)' }}>
                <Table.HeaderCell style={celulaCabecalho}>Nome</Table.HeaderCell>
                <Table.HeaderCell style={celulaCabecalho}>CPF</Table.HeaderCell>
                <Table.HeaderCell style={celulaCabecalho}>Participação</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {linhas.map((item, indice) => (
                <Table.Row key={`${item.cpf}-${item.nome}-${indice}`}>
                  <Table.Cell style={celula}>{valorOuTraco(item.nome)}</Table.Cell>
                  <Table.Cell style={celula}>{valorOuTraco(item.cpf)}</Table.Cell>
                  <Table.Cell style={celula}>{valorOuTraco(item.participacao)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      </div>
    </>
  );
};

const TabelaLinhas: React.FC<{ contrato: ColetaFluxoContrato }> = ({ contrato }) => (
  <div style={{ overflowX: 'auto', marginTop: 8 }}>
    <div style={{ background: '#fff', borderRadius: 8 }}>
      <Table celled striped unstackable>
        <Table.Header>
          <Table.Row style={{ background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)' }}>
            <Table.HeaderCell style={celulaCabecalho}>DDD</Table.HeaderCell>
            <Table.HeaderCell style={celulaCabecalho}>Número</Table.HeaderCell>
            <Table.HeaderCell style={celulaCabecalho}>Ativação</Table.HeaderCell>
            <Table.HeaderCell style={celulaCabecalho}>Fidelidade</Table.HeaderCell>
            <Table.HeaderCell style={celulaCabecalho}>Semáforo</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {contrato.linhas.map((linha) => {
            const { ddd, numero } = separarDdd(linha.numero);
            return (
              <Table.Row key={`${linha.numero}-${linha.plano}-${linha.ativacao || ''}`}>
                <Table.Cell style={celula}>{ddd}</Table.Cell>
                <Table.Cell style={celula}>{numero}</Table.Cell>
                <Table.Cell style={celula}>{textoDaAtivacao(linha.ativacao)}</Table.Cell>
                <Table.Cell style={celula}>{textoDaFidelidade(linha.parcelasRestantes)}</Table.Cell>
                <Table.Cell style={celula}><Bolinha cor={linha.semaforo} /></Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  </div>
);

const RotuloRegra: React.FC<{ rotulo: string; descricao?: string }> = ({ rotulo, descricao: descricaoInformada }) => {
  const descricao = descricaoInformada || descricaoDaRegra(rotulo);
  const label = (
    <strong style={{ cursor: descricao ? 'help' : undefined, borderBottom: descricao ? '1px dotted #2c3e50' : undefined }}>
      {rotulo}:
    </strong>
  );
  if (!descricao) return label;
  return (
    <Popup
      trigger={label}
      content={descricao}
      position="top left"
      size="small"
      style={{ zIndex: 10000, maxWidth: 320 }}
      mouseEnterDelay={200}
    />
  );
};

const iconeDoResumo = (tom: ItemResumoQualidade['tom']) => {
  if (tom === 'ok') return { name: 'check circle' as const, color: 'green' as const };
  if (tom === 'divergente') return { name: 'times circle' as const, color: 'red' as const };
  return { name: 'info circle' as const, color: 'grey' as const };
};

export const ContractColetaPainel: React.FC<{
  cnpj: string;
  linhas?: LinhaResumoQualidade[];
  telefoneAdministrador?: string | null;
  telefoneAdministrador2?: string | null;
  contractId?: string;
  groupId?: string | null;
  idAditivo?: string | null;
  idRenegociacao?: string | null;
  status?: number | string | null;
  podeAcionarColeta?: boolean;
}> = ({
  cnpj,
  linhas,
  telefoneAdministrador,
  telefoneAdministrador2,
  contractId,
  groupId,
  idAditivo,
  idRenegociacao,
  status,
  podeAcionarColeta = false,
}) => {
  const digitos = cnpj.replace(/\D/g, '');
  const chaveVtme = chaveVtmeDe(contractId, groupId);
  const temIdsVtme = temIdsPedidoVtme(idAditivo, idRenegociacao);
  const [fluxo, setFluxo] = useState<ColetaFluxo | null>(null);
  const [erro, setErro] = useState('');
  const [oculto, setOculto] = useState(false);
  const [priorizando, setPriorizando] = useState(false);
  const [retentando, setRetentando] = useState(false);
  const [pedindo, setPedindo] = useState<'coletar' | 'atualizar' | 'p2b' | 'lemit' | null>(null);
  const [pedindoFatura, setPedindoFatura] = useState(false);
  const [erroFatura, setErroFatura] = useState('');
  const [abertos, setAbertos] = useState<Record<string, boolean>>({});
  const [vtme, setVtme] = useState<ColetaJob | null>(null);
  const [erroVtme, setErroVtme] = useState('');
  const [retentandoVtme, setRetentandoVtme] = useState(false);

  useEffect(() => {
    if (digitos.length !== 14) return undefined;
    let vivo = true;
    const ler = async () => {
      try {
        const atual = await consultarFluxo(digitos);
        if (!vivo) return;
        setFluxo(atual);
        if (fluxoColetaAceito(atual)) setErro('');
      } catch (falha) {
        if (!vivo) return;
        if (statusDe(falha) === 403) {
          setOculto(true);
          return;
        }
        setErro(erroDeColeta(falha, 'Não foi possível consultar a coleta.'));
      }
    };
    void ler();
    const timer = window.setInterval(() => { void ler(); }, INTERVALO_MS);
    return () => {
      vivo = false;
      window.clearInterval(timer);
    };
  }, [digitos]);

  useEffect(() => {
    setAbertos({});
  }, [digitos]);

  useEffect(() => {
    if (!chaveVtme) {
      setVtme(null);
      setErroVtme('');
      return undefined;
    }
    let vivo = true;
    const ler = async () => {
      try {
        const atual = await consultarVtme(chaveVtme);
        if (!vivo) return;
        setVtme(atual);
        setErroVtme('');
      } catch (falha) {
        if (!vivo) return;
        if (statusDe(falha) === 404) {
          setVtme(null);
          setErroVtme('');
          return;
        }
        if (statusDe(falha) === 403) return;
        setErroVtme(erroDeColeta(falha, 'Não foi possível consultar o VTME.'));
      }
    };
    void ler();
    const timer = window.setInterval(() => { void ler(); }, INTERVALO_MS);
    return () => {
      vivo = false;
      window.clearInterval(timer);
    };
  }, [chaveVtme]);

  if (digitos.length !== 14 || oculto) return null;

  const priorizar = async () => {
    setPriorizando(true);
    try {
      setFluxo(await priorizarFluxo(digitos));
      setErro('');
    } catch (falha) {
      setErro(erroDeColeta(falha, 'Não foi possível priorizar a coleta.'));
    } finally {
      setPriorizando(false);
    }
  };

  const enfileirarVtme = async (forcar: boolean) => {
    if (!chaveVtme) return;
    if (!deveEnfileirarVtme({
      temIds: temIdsVtme,
      temResultado: jobVtmeComResultado(vtme),
      forcar,
      aberto: jobVtmeAberto(vtme),
    })) return;
    try {
      setVtme(await retentarVtme(chaveVtme));
      setErroVtme('');
    } catch (falha) {
      setErroVtme(erroDeColeta(falha, 'Não foi possível pedir a conferência do VTME.'));
    }
  };

  const retentar = async () => {
    setRetentando(true);
    try {
      const atual = await retentarFluxo(digitos);
      setFluxo(atual);
      if (!fluxoColetaAceito(atual)) {
        setErro(MENSAGEM_COLETA_SEM_FILA);
      } else {
        setErro('');
        await enfileirarVtme(false);
      }
    } catch (falha) {
      setErro(erroDeColeta(falha, 'Não foi possível tentar de novo.'));
    } finally {
      setRetentando(false);
    }
  };

  const telefonesDoContrato = [telefoneAdministrador, telefoneAdministrador2]
    .map((valor) => (valor || '').trim())
    .filter(Boolean);

  const pedir = async (acao: 'coletar' | 'atualizar' | 'p2b' | 'lemit') => {
    setPedindo(acao);
    try {
      const origem = acao === 'atualizar' ? 'todas' : acao === 'p2b' || acao === 'lemit' ? acao : undefined;
      const atual = await pedirFluxo({
        cnpj: digitos,
        telefones: telefonesDoContrato,
        forcar: true,
        ...(contractId ? { contractId } : {}),
        ...(origem ? { origem } : {}),
      });
      setFluxo(atual);
      if (!fluxoColetaAceito(atual)) {
        setErro(MENSAGEM_COLETA_SEM_FILA);
        return;
      }
      setErro('');
      if (acao === 'coletar') await enfileirarVtme(false);
    } catch (falha) {
      setErro(erroDeColeta(
        falha,
        acao === 'coletar' ? 'Não foi possível pedir a coleta.' : 'Não foi possível atualizar a coleta.',
      ));
    } finally {
      setPedindo(null);
    }
  };

  const pedirFatura = async () => {
    setPedindoFatura(true);
    try {
      setFluxo(await pedirFluxo({
        cnpj: digitos,
        telefones: telefonesDoContrato,
        forcar: true,
        forcarFatura: true,
      }));
      setErroFatura('');
    } catch (falha) {
      setErroFatura(erroDeColeta(falha, 'Não foi possível coletar os dados da fatura.'));
    } finally {
      setPedindoFatura(false);
    }
  };

  const resultado = fluxo?.resultado ?? null;
  const ocupado = filaColetaOcupada(fluxo?.fila);
  const botoes = visibilidadeBotoesColeta({
    temCnpj: digitos.length === 14,
    podeAcionar: podeAcionarColeta,
    fluxoCarregado: Boolean(fluxo),
    temResultado: temDadoDeColeta(fluxo),
    ocupado,
  });
  const acaoEmCurso = priorizando || retentando || Boolean(pedindo) || pedindoFatura || retentandoVtme;
  const contratos = resultado ? blocos(resultado) : [];
  const visaoFatura = visaoFaturaP2b(estadoDaFatura(resultado), {
    podeAcionar: podeAcionarColeta,
    inadimplente: resultado?.inadimplente,
    temContatos: Boolean(resultado?.contatos?.length) || contratos.some((contrato) => (contrato.contatos ?? []).length > 0),
    temLemit: Boolean(resultado?.telefoneLemit)
      || contratos.some((contrato) => contrato.telefoneLemit)
      || temDadoLemit(resultado?.lemit),
  });
  const semDados = fluxo && !fluxo.fila && !resultado;
  const telefoneLemitBruto = resultado?.telefoneLemit
    || contratos.find((contrato) => contrato.telefoneLemit)?.telefoneLemit
    || null;
  const lemitTela = lemitParaTela(resultado?.lemit, telefoneLemitBruto);
  const numerosLemit = numerosDoLemit(resultado?.lemit, telefoneLemitBruto);
  const temLemitConsultado = temResultadoLemit(resultado?.lemit, telefoneLemitBruto);
  const linhasP2bResumo = [
    ...(resultado?.linhas ?? []),
    ...contratos.flatMap((contrato) => contrato.linhas ?? []),
  ];
  const contatosP2bResumo = [
    ...(resultado?.contatos ?? []),
    ...contratos.flatMap((contrato) => contrato.contatos ?? []),
  ];
  const ladosAbertos = ladosDoContratoAberto({
    idAditivo,
    idRenegociacao,
    linhasContrato: linhas,
  });
  const mostraAditivo = ladosAbertos.aditivo;
  const mostraRenegociacao = ladosAbertos.renegociacao;
  const paineisPedidoVtme = paineisVtme({
    mostraAditivo,
    mostraRenegociacao,
    aditivo: vtme?.resultado?.aditivo,
    renegociacao: vtme?.resultado?.renegociacao,
    idAditivo,
    idRenegociacao,
  });
  const resumoP2b = resultado
    ? resumoQualidade({
      linhasContrato: linhas,
      telefonesAdministrador: [telefoneAdministrador, telefoneAdministrador2],
      linhasP2b: linhasP2bResumo,
      contatosP2b: contatosP2bResumo,
      telefonesLemit: numerosLemit,
      temResultadoLemit: temLemitConsultado,
      inadimplente: resultado.inadimplente,
    })
    : [];
  const resumoVtmeItens = jobVtmeComResultado(vtme) && isVtmeCriadoOrLaterPipelineStatus(status)
    ? resumoVtmeAgrupado({
      aditivo: mostraAditivo ? vtme?.resultado?.aditivo : null,
      renegociacao: mostraRenegociacao ? vtme?.resultado?.renegociacao : null,
      mesmoPedido: Boolean(vtme?.resultado?.mesmoPedido),
    })
    : [];
  const resumo = [...resumoP2b, ...resumoVtmeItens];
  const temTelefoneLemit = numerosLemit.length > 0;
  const adminNoP2b = adminEncontradoNoP2b({
    telefonesAdministrador: [telefoneAdministrador, telefoneAdministrador2],
    linhasP2b: linhasP2bResumo,
    contatosP2b: contatosP2bResumo,
  });
  const mostraLemit = mostrarSecaoLemit(adminNoP2b);
  const adminAusenteNoP2b = Boolean(
    resultado
    && !resultado.inadimplente
    && resumoP2b.some((item) => item.rotulo === 'Administrador' && item.tom === 'divergente'),
  );
  const lemitAindaEsperado = fluxo?.lemitPendente === true
    || (fluxo?.lemitPendente !== false && !temLemitConsultado && adminAusenteNoP2b && !temTelefoneLemit);
  const avisoLemit = avisoLemitQualidade({
    temResultadoP2b: Boolean(resultado),
    temTelefoneLemit,
    lemitAindaEsperado,
    assinadoOuDepois: contratoAssinadoOuDepois(status),
    adminEncontradoNoP2b: adminNoP2b,
  });

  const repetirVtme = async () => {
    if (!chaveVtme || jobVtmeAberto(vtme)) return;
    setRetentandoVtme(true);
    try {
      setVtme(await retentarVtme(chaveVtme));
      setErroVtme('');
    } catch (falha) {
      setErroVtme(erroDeColeta(falha, 'Não foi possível pedir nova conferência do VTME.'));
    } finally {
      setRetentandoVtme(false);
    }
  };

  const etapaFila = (fluxo?.fila?.etapa || '').toLowerCase();
  const mostraAtualizarP2b = visibilidadeBotaoAtualizarOrigem({
    podeAcionar: podeAcionarColeta,
    temSecao: Boolean(resultado),
    ocupado: ocupado && (etapaFila === 'p2b' || etapaFila === ''),
  });
  const mostraAtualizarLemit = visibilidadeBotaoAtualizarOrigem({
    podeAcionar: podeAcionarColeta,
    temSecao: mostraLemit,
    ocupado: ocupado && etapaFila === 'lemit',
  });
  const mostraAtualizarVtme = visibilidadeBotaoAtualizarOrigem({
    podeAcionar: podeAcionarColeta,
    temSecao: jobVtmeComResultado(vtme),
    ocupado: jobVtmeAberto(vtme),
  });

  return (
    <Segment style={caixa}>
      <strong style={titulo}>
        <Icon name="clipboard list" style={{ fontSize: '1.1em' }} />
        Qualidade
      </strong>
      {erro ? <Message negative content={erro} /> : null}
      {erroVtme ? <Message negative content={erroVtme} /> : null}
      {!fluxo && !erro ? <p style={texto}>Carregando a qualidade.</p> : null}
      {fluxo?.fila ? <p style={texto}>{textoDaFila(fluxo.fila)}</p> : null}
      {semDados ? <p style={texto}>Ainda não há dados da coleta para este CNPJ.</p> : null}
      {visibilidadeBotaoPriorizar(podeAcionarColeta, Boolean(fluxo?.fila?.podePriorizar)) ? (
        <Button type="button" size="small" onClick={() => { void priorizar(); }} loading={priorizando} disabled={acaoEmCurso}>
          Priorizar
        </Button>
      ) : null}
      {podeAcionarColeta && (falhou(fluxo) || botoes.retentar) ? (
        <Button
          type="button"
          size="small"
          onClick={() => { void (falhou(fluxo) ? retentar() : pedir('coletar')); }}
          loading={retentando || pedindo === 'coletar'}
          disabled={acaoEmCurso}
        >
          Tentar novamente
        </Button>
      ) : null}
      {botoes.atualizar ? (
        <Button type="button" size="small" onClick={() => { void pedir('atualizar'); }} loading={pedindo === 'atualizar'} disabled={acaoEmCurso}>
          Atualizar a coleta
        </Button>
      ) : null}
      {resumo.length > 0 || resultado ? (
        <div style={texto}>
          {resumo.length > 0 ? (
          <div style={secaoP2b}>
            <strong style={titulo}>
              <Icon name="clipboard check" />
              Resumo
            </strong>
            {resumo.map((item, indice) => {
              const icone = iconeDoResumo(item.tom);
              const bullets = item.itens && item.itens.length >= 2 ? item.itens : null;
              const detalhe = bullets ? undefined : (item.itens?.[0] ?? item.texto);
              return (
                <div
                  key={`${item.rotulo}-${indice}`}
                  style={{ marginTop: 10, display: 'flex', alignItems: bullets ? 'flex-start' : 'center', gap: 8, lineHeight: 1 }}
                >
                  <Icon
                    name={icone.name}
                    color={icone.color}
                    style={{
                      margin: 0,
                      lineHeight: 1,
                      height: '1em',
                      width: '1em',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ lineHeight: 1.35 }}>
                    <RotuloRegra rotulo={item.rotulo} descricao={item.descricao} />
                    {bullets ? (
                      <ul style={{ margin: '6px 0 0 1.25em', padding: 0 }}>
                        {bullets.map((bullet) => (
                          <li key={bullet}>{bullet}</li>
                        ))}
                      </ul>
                    ) : (
                      <> <span>{detalhe}</span></>
                    )}
                  </div>
                </div>
              );
            })}
            {avisoLemit ? <p style={texto}>{avisoLemit}</p> : null}
          </div>
          ) : null}
          {resultado ? (
          <>
          {contratos.length > 0 ? (
            <div style={secaoP2b}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 12, lineHeight: 1 }}>
                <strong style={{ ...titulo, display: 'flex', alignItems: 'center', gap: 8, lineHeight: 1 }}>
                  <Icon
                    name="sitemap"
                    style={{
                      margin: 0,
                      lineHeight: 1,
                      height: '1em',
                      width: '1em',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ lineHeight: 1 }}>P2B</span>
                </strong>
                <span style={{ lineHeight: 1, fontSize: '0.95em', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Coletado em: {textoDaData(resultado)}
                </span>
                {mostraAtualizarP2b ? (
                  <Button
                    type="button"
                    size="small"
                    compact
                    aria-label="Atualizar coleta P2B"
                    onClick={() => { void pedir('p2b'); }}
                    loading={pedindo === 'p2b'}
                    disabled={acaoEmCurso}
                  >
                    Atualizar coleta
                  </Button>
                ) : null}
              </div>
              {contratos.map((contrato, indice) => {
                const chave = `${contrato.custcode || 'sem'}-${indice}`;
                const aberto = Boolean(abertos[chave]);
                const semLinhas = (contrato.linhas ?? []).length === 0;
                const mostrarFatura = visaoFatura.mostrar && (indice === 0 || visaoFatura.mostrarTabela);
                const rotulo = contrato.custcode ? `CustCode ${contrato.custcode}` : 'Contrato';
                return (
                  <div key={chave} style={secaoCustcode}>
                    <button
                      type="button"
                      aria-expanded={aberto}
                      onClick={() => setAbertos((atual) => ({ ...atual, [chave]: !atual[chave] }))}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: 8,
                        textAlign: 'left',
                        lineHeight: 1,
                        background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)',
                        border: 'none',
                        padding: '12px 14px',
                        cursor: 'pointer',
                        fontWeight: 700,
                        color: '#2c3e50',
                        fontSize: '1em',
                      }}
                    >
                      <Icon
                        name={aberto ? 'chevron down' : 'chevron right'}
                        style={{
                          margin: 0,
                          lineHeight: 1,
                          height: '1em',
                          width: '1em',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      />
                      <span style={{ lineHeight: 1 }}>{rotulo}</span>
                    </button>
                    {aberto ? (
                      <div style={{ padding: 14 }}>
                        {resultado.inadimplente ? (
                          <Label color="red" size="medium">
                            <Icon name="times circle" />
                            Inadimplente
                          </Label>
                        ) : null}
                        {mostrarFatura ? (
                          <>
                            <div style={{ marginTop: 10 }}>Dados da fatura</div>
                            {indice === 0 && visaoFatura.mensagem ? <p style={texto}>{visaoFatura.mensagem}</p> : null}
                            {indice === 0 && erroFatura ? <Message negative content={erroFatura} /> : null}
                            {visaoFatura.mostrarTabela ? <TabelaFatura contrato={contrato} /> : null}
                            {indice === 0 && visaoFatura.mostrarBotao ? (
                              <Button
                                type="button"
                                size="small"
                                onClick={() => { void pedirFatura(); }}
                                loading={pedindoFatura}
                                disabled={acaoEmCurso || ocupado}
                              >
                                Coletar dados da fatura
                              </Button>
                            ) : null}
                          </>
                        ) : null}
                        {semLinhas ? null : (
                          <>
                            <div style={{ marginTop: 10 }}>Dados das linhas</div>
                            <TabelaLinhas contrato={contrato} />
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span>Coletado em: {textoDaData(resultado)}</span>
              {mostraAtualizarP2b ? (
                <Button
                  type="button"
                  size="small"
                  compact
                  aria-label="Atualizar coleta P2B"
                  onClick={() => { void pedir('p2b'); }}
                  loading={pedindo === 'p2b'}
                  disabled={acaoEmCurso}
                >
                  Atualizar coleta
                </Button>
              ) : null}
            </div>
          )}
          {mostraLemit ? (
          <div style={secaoP2b}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 12, lineHeight: 1 }}>
              <strong style={{ ...titulo, display: 'flex', alignItems: 'center', gap: 8, lineHeight: 1 }}>
                <Icon
                  name="id card"
                  style={{
                    margin: 0,
                    lineHeight: 1,
                    height: '1em',
                    width: '1em',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                />
                <span style={{ lineHeight: 1 }}>Lemmit</span>
              </strong>
              <span style={{ lineHeight: 1, fontSize: '0.95em', fontWeight: 600, whiteSpace: 'nowrap' }}>
                Coletado em: {textoDaData(lemitTela)}
              </span>
              {mostraAtualizarLemit ? (
                <Button
                  type="button"
                  size="small"
                  compact
                  aria-label="Atualizar coleta Lemmit"
                  onClick={() => { void pedir('lemit'); }}
                  loading={pedindo === 'lemit'}
                  disabled={acaoEmCurso}
                >
                  Atualizar coleta
                </Button>
              ) : null}
            </div>
            {temDadoLemit(lemitTela) && lemitTela ? (
              <>
                <TabelaLemitDados lemit={lemitTela} />
                <TabelaLemitTelefones
                  lemit={lemitTela}
                  telefonesAdministrador={[telefoneAdministrador, telefoneAdministrador2].map((valor) => (valor || '').trim()).filter(Boolean)}
                />
                <TabelaLemitSocios lemit={lemitTela} />
              </>
            ) : (
              <p style={texto}>{textoVazioLemit({
                temResultadoLemit: temLemitConsultado,
                lemitAindaEsperado,
                assinadoOuDepois: contratoAssinadoOuDepois(status),
              })}</p>
            )}
          </div>
          ) : null}
          </>
          ) : null}
        </div>
      ) : null}
      {jobVtmeComResultado(vtme) ? (
        <div style={secaoP2b}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 12, lineHeight: 1 }}>
            <strong style={{ ...titulo, display: 'flex', alignItems: 'center', gap: 8, lineHeight: 1 }}>
              <Icon name="check square" style={{ margin: 0 }} />
              <span style={{ lineHeight: 1 }}>VTME</span>
            </strong>
            <span style={{ lineHeight: 1, fontSize: '0.95em', fontWeight: 600, whiteSpace: 'nowrap' }}>
              Coletado em: {textoDaData({ buscadoEm: vtme?.encerradoEm ?? vtme?.criadoEm })}
            </span>
            {mostraAtualizarVtme ? (
              <Button
                type="button"
                size="small"
                compact
                aria-label="Atualizar coleta VTME"
                onClick={() => { void repetirVtme(); }}
                loading={retentandoVtme}
                disabled={acaoEmCurso || jobVtmeAberto(vtme)}
              >
                Atualizar coleta
              </Button>
            ) : null}
          </div>
          {jobVtmeAberto(vtme) ? <p style={texto}>Conferindo o pedido no VTME.</p> : null}
          {paineisPedidoVtme.map((painel) => {
            const aberto = Boolean(abertos[painel.chave]);
            return (
              <AcordeaoVtme
                key={painel.chave}
                rotulo={painel.rotulo}
                aberto={aberto}
                onToggle={() => setAbertos((atual) => ({ ...atual, [painel.chave]: !atual[painel.chave] }))}
              >
                {painel.lados.map((item) => (
                  <LadoVtme
                    key={item.ladoNome}
                    lado={item.lado}
                    rotuloLado={item.rotuloInterno}
                    cnpjContrato={vtme?.resultado?.cnpj || digitos}
                    linhasContrato={linhas}
                    ladoNome={item.ladoNome}
                  />
                ))}
              </AcordeaoVtme>
            );
          })}
        </div>
      ) : null}
    </Segment>
  );
};
